import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

import { Map as MaplibreMap } from "maplibre-gl";
import {
  Feature,
  FeatureCollection,
  GeoJsonProperties,
  Geometry,
  LineString,
  Polygon,
  Position
} from "geojson";

import {
  CATCHMENT_DATASET_POLYGON_FILL_COLOR,
  CATCHMENT_DATASET_POLYGON_FILL_OPACITY,
  CATCHMENT_DATASET_POLYGON_SOURCE_ID,
  CIRCE_RADIUS_SOURCE_ID,
  ROUTING_SOURCE_ID
} from "@/constants/map";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { RootState } from "@/store";
import { Dataset } from "@/types/dataset";
import { MapStyleProperties, MarkerPinItem } from "@/types/map";
import {
  AreaInputType,
  CatchmentDataset,
  CatchmentInputBy,
  ECatchmentInputBy,
  ECatchmentType
} from "@/types/spatial";
import {
  extractCoordinatesFromDatasets,
  getVisibleCatchmentDatasets
} from "@/utils/catchment";
import { convertDatasetResponseToFeatureCollection } from "@/utils/dataset";
import {
  addPolygonLayer,
  addRoutingLayer,
  removeAllSourceLayerWithPrefix
} from "@/utils/map";
import {
  addCentroidSource,
  addPolygonCatchmentSource
} from "@/utils/map/polygon";

import { circle, lineString, lineToPolygon } from "@turf/turf";

type UseCatchmentAreaProps = {
  map: MaplibreMap;
  isMapLoaded: boolean;
  removeAllMarkers: () => void;
  handleAddPolygonLayer: (
    dataset: Dataset,
    polygonData: FeatureCollection<Geometry>,
    customId?: string
  ) => void;
  handleAddMarkers: (
    data: { name?: string; coordinates: [number, number] | Position }[]
  ) => void;
};

const isValidLineString = (geometry: Geometry | undefined): boolean => {
  return !!(
    geometry &&
    geometry?.type === "LineString" &&
    geometry?.coordinates.length >= 2
  );
};

export const useCatchmentArea = ({
  map,
  isMapLoaded,
  removeAllMarkers,
  handleAddPolygonLayer,
  handleAddMarkers
}: UseCatchmentAreaProps) => {
  const [searchParams] = useSearchParams();
  const isPreviewMode = searchParams.get("preview") === "true";
  const {
    catchmentSettings,
    spatialConfig: { areaInput: spatialAreaInputType },
    catchmentSettingsTemp
  } = useSelector((state: RootState) => state.spatial);

  const {
    showCatchmentPopup,
    showAnalysisResultPopup,
    showComparisonPopup,
    showAnalysisSummaryPopup,
    showSearchLocationPopup,
    isDrawPolygon
  } = useSelector((state: RootState) => state.ui);
  const { isBVTDataForCatchmentEnabled } = useAppFeatures();

  /** Polygon catchment layers live only inside the BVT multi-dataset flow
   * AND only while the user picked BY_DATASET as the catchment input mode.
   * Keep the input-mode check explicit so that, when the FF is eventually
   * cleaned up, the layer lifecycle stays scoped to BY_DATASET and doesn't
   * bleed into BY_LOCATION / other modes.
   */
  const isPolygonDatasetCatchment =
    isBVTDataForCatchmentEnabled &&
    catchmentSettings?.catchmentInputBy === ECatchmentInputBy.BY_DATASET;

  const handleAddCatchmentLayer = async (
    dataset: Dataset,
    polygonData: FeatureCollection<Geometry>
  ) => {
    if (!polygonData || !map) return;

    removeAllMarkers();

    const polygonDataCatchment = {
      type: "FeatureCollection",
      features: polygonData.features.map((feature) => ({
        ...feature,
        geometry: feature.properties?.catchment_geometry,
        properties: {
          ...Object.fromEntries(
            Object.entries(feature.properties || {}).filter(
              ([key]) =>
                !["catchment_geometry", "location_geometry"].includes(key)
            )
          )
        }
      }))
    } as FeatureCollection<Geometry>;

    handleAddPolygonLayer(dataset, polygonDataCatchment);
    handleAddMarkers(
      polygonData.features.map((feature) => ({
        coordinates: feature.properties?.location_geometry
          .coordinates as unknown as [number, number]
      }))
    );
  };

  const handleAddRegionLayer = (
    sourceId: string,
    polygonData:
      | FeatureCollection<Geometry>
      | Feature<Polygon, GeoJsonProperties>,
    styleProperties?: MapStyleProperties
  ) => {
    if (!map) return;
    if (map.getSource(sourceId)) return;
    addPolygonCatchmentSource(map, sourceId, polygonData);
    addPolygonLayer(map, sourceId, styleProperties);
  };

  /**  Renders polygon-typed catchment datasets as flat-blue polygon layers
   * (matching the polygon-draw style: #4C7DFB, 0.4 opacity) plus a centroid
   * symbol layer that labels each polygon "Location layer polygon #N" using
   * a global 1-based counter across all visible polygon datasets. Clean-slate
   * removal of all CATCHMENT_DATASET_POLYGON_SOURCE_ID-* sources/layers, then
   * re-adds the visible ones. Add/remove/visibility edits are driven by
   * Redux state via the effects below.
   */
  const renderPolygonCatchmentDatasets = (
    datasets: Record<string, CatchmentDataset>
  ) => {
    if (!map) return;
    /** Wipe any previous polygon catchment layers (incl. their centroid label
     * layers) before deciding whether to re-render. Doing this unconditionally
     * ensures the layers are reactively removed when showAnalysisResultPopup
     * toggles on — even before useMapLayerInitialization's parallel cleanup
     * fires — and prevents stale labels from outliving their dataset.
     */
    removeAllSourceLayerWithPrefix(
      map,
      CATCHMENT_DATASET_POLYGON_SOURCE_ID,
      []
    );
    /** Analysis result owns the map surface — catchment input layers (fills
     * AND labels) are pre-analysis decoration and must not bleed through
     * the analysis output (H3/geohash grid, score layers, etc.).
     */
    if (showAnalysisResultPopup) return;
    const visibleEntries = getVisibleCatchmentDatasets(datasets);
    let polygonLabelCounter = 0;
    for (const entry of visibleEntries) {
      const { dataset, rawDataset } = entry;
      if (dataset.data.spatial_aggregation_type !== "polygon") continue;
      const baseFeatureCollection =
        convertDatasetResponseToFeatureCollection(rawDataset);
      const labeledFeatureCollection: FeatureCollection<Geometry> = {
        ...baseFeatureCollection,
        features: baseFeatureCollection.features.map((feature) => {
          polygonLabelCounter += 1;
          return {
            ...feature,
            properties: {
              ...feature.properties,
              catchmentPolygonLabel: `Location layer polygon #${polygonLabelCounter}`
            }
          };
        })
      };

      const sourceId = `${CATCHMENT_DATASET_POLYGON_SOURCE_ID}-${dataset.data.id}`;
      handleAddRegionLayer(sourceId, labeledFeatureCollection, {
        fillColor: CATCHMENT_DATASET_POLYGON_FILL_COLOR,
        fillOpacity: CATCHMENT_DATASET_POLYGON_FILL_OPACITY
      });

      /** Centroid + symbol label layer so each polygon is annotated. We don't
       * re-check showAnalysisResultPopup here because the early-return above
       * already short-circuits this whole block when the analysis result
       * popup is open; the wipe at the top of the function ensures previously
       * rendered labels are removed when the flag transitions to true.
       */
      addCentroidSource(map, `${sourceId}-centroid`, labeledFeatureCollection);
      if (!map.getLayer(`${sourceId}-label`)) {
        map.addLayer({
          id: `${sourceId}-label`,
          type: "symbol",
          source: `${sourceId}-centroid`,
          layout: {
            "text-field": ["get", "catchmentPolygonLabel"],
            "text-font": ["Open Sans Bold", "Arial Unicode MS Regular"],
            "text-size": 12,
            "text-anchor": "center",
            "text-allow-overlap": false,
            "text-pitch-alignment": "viewport",
            "text-rotation-alignment": "viewport"
          },
          paint: {
            "text-color": "#000000",
            "text-halo-color": "#FFFFFF",
            "text-halo-width": 1.2
          }
        });
      }
    }
  };

  // TODO: @vudh15 refactor for better naming
  const handleAddIsochroneLayer = (
    sourceId: string,
    polygonData:
      | FeatureCollection<Geometry>
      | Feature<Polygon, GeoJsonProperties>,
    styleProperties?: MapStyleProperties
  ) => {
    if (!map) return;
    if (map.getSource(sourceId)) return;
    addPolygonCatchmentSource(map, sourceId, polygonData);
    addRoutingLayer(map, sourceId, styleProperties);
  };

  const removeCatchmentMapDisplay = () => {
    removeAllMarkers();
    // Remove all region polygon
    removeAllSourceLayerWithPrefix(map, CIRCE_RADIUS_SOURCE_ID, []);
    removeAllSourceLayerWithPrefix(map, ROUTING_SOURCE_ID, []);
    /** Polygon catchment dataset layers must follow the catchment input
     * lifecycle — when the catchment display is being wiped (e.g. switching
     * to admin-area mode or entering preview), wipe these too. Only relevant
     * inside the BVT multi-dataset catchment flow with BY_DATASET input.
     */
    if (isPolygonDatasetCatchment) {
      removeAllSourceLayerWithPrefix(
        map,
        CATCHMENT_DATASET_POLYGON_SOURCE_ID,
        []
      );
    }
  };

  const handleAddCircleRadius = (
    catchmentType: string,
    item: MarkerPinItem,
    radiusData: string[]
  ) => {
    if (catchmentType === ECatchmentType.BY_RADIUS) {
      const center = [item.lng, item.lat];

      removeAllSourceLayerWithPrefix(
        map,
        `${CIRCE_RADIUS_SOURCE_ID}-${item.id}`,
        radiusData?.map((radius) => `${radius}meter`) || []
      );

      const radiusValid = radiusData.filter((item) => item !== " ");
      radiusValid.forEach((radius) => {
        const circleArea = circle(center, Number.parseInt(radius), {
          steps: 64,
          units: "meters"
        });
        handleAddIsochroneLayer(
          `${CIRCE_RADIUS_SOURCE_ID}-${item.id}-${radius}meter`,
          circleArea
        );
      });
    }
  };

  const handleAddCircleRadiusOriginal = (
    catchmentInputBy?: CatchmentInputBy
  ) => {
    const radiusData = catchmentSettings?.radiusData ?? [];
    const catchmentType = catchmentSettings?.catchmentType;
    const isCatchment = spatialAreaInputType?.value === AreaInputType.CATCHMENT;

    if (
      !isMapLoaded ||
      !map ||
      !isCatchment ||
      showCatchmentPopup ||
      showAnalysisResultPopup ||
      showComparisonPopup.isShow ||
      showAnalysisSummaryPopup ||
      showSearchLocationPopup ||
      isPreviewMode
    )
      return;

    removeAllSourceLayerWithPrefix(map, ROUTING_SOURCE_ID, []);
    removeAllSourceLayerWithPrefix(map, CIRCE_RADIUS_SOURCE_ID, []);

    if (catchmentInputBy === ECatchmentInputBy.BY_LOCATION) {
      const coordinates = catchmentSettings?.placeCoordinates ?? [];

      for (const item of coordinates) {
        // Handle add circle radius
        handleAddCircleRadius(catchmentType as string, item, radiusData);
      }
    } else {
      const datasets = catchmentSettings?.datasetConfig?.datasets ?? {};
      const coordinates = extractCoordinatesFromDatasets(
        Object.values(datasets)
      );

      for (const item of coordinates) {
        handleAddCircleRadius(catchmentType as string, item, radiusData);
      }
    }
  };

  const handleAddCircleRadiusTemp = (catchmentInputBy?: CatchmentInputBy) => {
    const radiusDataTemp = catchmentSettingsTemp?.radiusData ?? [];
    const catchmentTypeTemp = catchmentSettingsTemp.catchmentType;
    const isCatchment = spatialAreaInputType?.value === AreaInputType.CATCHMENT;

    if (!isMapLoaded || !map || !isCatchment || !showCatchmentPopup) return;

    removeAllSourceLayerWithPrefix(map, CIRCE_RADIUS_SOURCE_ID, []);

    if (catchmentTypeTemp === ECatchmentType.BY_RADIUS) {
      if (catchmentInputBy === ECatchmentInputBy.BY_LOCATION) {
        const coordinatesTemp = catchmentSettingsTemp.placeCoordinates ?? [];
        for (const item of coordinatesTemp) {
          // Handle add circle radius
          handleAddCircleRadius(
            catchmentSettingsTemp.catchmentType as string,
            item,
            radiusDataTemp
          );
        }
      } else {
        const datasets = catchmentSettingsTemp.datasetConfig?.datasets ?? {};
        const visibleEntries = getVisibleCatchmentDatasets(datasets);
        const coordinatesTemp = extractCoordinatesFromDatasets(visibleEntries);

        for (const item of coordinatesTemp) {
          handleAddCircleRadius(
            catchmentTypeTemp as string,
            item,
            radiusDataTemp
          );
        }
      }
    }
  };

  const handleAddIsochrone = () => {
    const catchmentType = catchmentSettings?.catchmentType;
    const isCatchment = spatialAreaInputType?.value === AreaInputType.CATCHMENT;
    const isochroneData =
      catchmentSettings?.roadAccessConfig?.isochroneData ?? [];
    const isochroneDataMapping = isochroneData.map((item) => item?.data).flat();

    if (
      !isMapLoaded ||
      !map ||
      !isCatchment ||
      catchmentType === ECatchmentType.BY_RADIUS ||
      showCatchmentPopup ||
      showAnalysisResultPopup ||
      showComparisonPopup.isShow ||
      showAnalysisSummaryPopup ||
      showSearchLocationPopup ||
      isPreviewMode
    )
      return;

    removeAllSourceLayerWithPrefix(map, ROUTING_SOURCE_ID, []);

    const isochroneDataValid = isochroneDataMapping
      .map((item) => item?.features)
      .flat();

    isochroneDataValid.forEach((item, index) => {
      if (isValidLineString(item.geometry)) {
        const line = lineString((item.geometry as LineString).coordinates);
        const polygon = lineToPolygon(line);
        handleAddIsochroneLayer(`${ROUTING_SOURCE_ID}-${index}`, {
          type: "FeatureCollection",
          features: [polygon]
        });
      }
    });
  };

  const handleAddIsochroneTemp = () => {
    const catchmentTypeTemp = catchmentSettingsTemp.catchmentType;
    const isCatchment = spatialAreaInputType?.value === AreaInputType.CATCHMENT;
    const isochroneData =
      catchmentSettingsTemp.roadAccessConfig?.isochroneData ?? [];

    const isochroneDataMapping = isochroneData
      .filter((item) => item.data)
      .map((item) => item.data)
      .flat();

    if (
      !isMapLoaded ||
      !map ||
      !isCatchment ||
      catchmentTypeTemp === ECatchmentType.BY_RADIUS ||
      !showCatchmentPopup
    )
      return;
    removeAllSourceLayerWithPrefix(map, ROUTING_SOURCE_ID, []);

    const isochroneDataValid = isochroneDataMapping
      .map((item) => item?.features)
      .flat();

    isochroneDataValid.forEach((item, index) => {
      if (isValidLineString(item.geometry)) {
        const line = lineString((item.geometry as LineString).coordinates);
        const polygon = lineToPolygon(line);
        handleAddIsochroneLayer(`${ROUTING_SOURCE_ID}-${index}`, {
          type: "FeatureCollection",
          features: [polygon]
        });
      }
    });
  };

  useEffect(() => {
    const catchmentType = catchmentSettings?.catchmentType;
    const catchmentTypeTemp = catchmentSettingsTemp.catchmentType;
    if (showCatchmentPopup) {
      removeAllSourceLayerWithPrefix(
        map,
        catchmentTypeTemp === ECatchmentType.BY_RADIUS
          ? ROUTING_SOURCE_ID
          : CIRCE_RADIUS_SOURCE_ID,
        []
      );
    } else {
      removeAllSourceLayerWithPrefix(
        map,
        catchmentType === ECatchmentType.BY_RADIUS
          ? ROUTING_SOURCE_ID
          : CIRCE_RADIUS_SOURCE_ID,
        []
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isMapLoaded,
    showCatchmentPopup,
    catchmentSettings?.catchmentType,
    catchmentSettingsTemp.catchmentType
  ]);

  // Syncs the COMMITTED catchment state (catchmentSettings) to map layers.
  // Active when the catchment popup is closed. Dispatches to exactly one
  // handler based on catchmentType. When polygon-draw mode is on (BVT flow),
  // the polygon flow owns the visualization, so both source prefixes are wiped.
  useEffect(() => {
    if (isBVTDataForCatchmentEnabled && isDrawPolygon && map) {
      removeAllSourceLayerWithPrefix(map, CIRCE_RADIUS_SOURCE_ID, []);
      removeAllSourceLayerWithPrefix(map, ROUTING_SOURCE_ID, []);
      removeAllSourceLayerWithPrefix(
        map,
        CATCHMENT_DATASET_POLYGON_SOURCE_ID,
        []
      );
      return;
    }
    // Polygon catchment dataset layers belong to the BY_DATASET tab only.
    // When the saved catchmentInputBy is anything else, wipe them so the map
    // matches the active input mode.
    if (isPolygonDatasetCatchment) {
      renderPolygonCatchmentDatasets(
        catchmentSettings?.datasetConfig?.datasets ?? {}
      );
    } else if (isBVTDataForCatchmentEnabled) {
      renderPolygonCatchmentDatasets({});
    }
    if (catchmentSettings?.catchmentType === ECatchmentType.BY_RADIUS) {
      handleAddCircleRadiusOriginal(catchmentSettings?.catchmentInputBy);
    } else {
      handleAddIsochrone();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isMapLoaded,
    showCatchmentPopup,
    spatialAreaInputType,
    catchmentSettings?.catchmentInputBy,
    catchmentSettings?.catchmentType,
    catchmentSettings?.radiusData,
    catchmentSettings?.placeCoordinates,
    catchmentSettings?.datasetConfig?.datasets,
    catchmentSettings?.roadAccessConfig?.isochroneData,
    showAnalysisResultPopup,
    showAnalysisSummaryPopup,
    showComparisonPopup,
    showSearchLocationPopup,
    isPreviewMode,
    isDrawPolygon
  ]);

  // Syncs the DRAFT catchment state (catchmentSettingsTemp) to map layers.
  // Active while the catchment popup is open so the user sees a live preview
  // of edits before committing. Same single-handler dispatch by catchmentType
  // as the committed-state effect above.
  useEffect(() => {
    if (isBVTDataForCatchmentEnabled && isDrawPolygon && map) {
      removeAllSourceLayerWithPrefix(map, CIRCE_RADIUS_SOURCE_ID, []);
      removeAllSourceLayerWithPrefix(map, ROUTING_SOURCE_ID, []);
      removeAllSourceLayerWithPrefix(
        map,
        CATCHMENT_DATASET_POLYGON_SOURCE_ID,
        []
      );
      return;
    }
    /** The temp render only owns the preview while the catchment popup is
     * open. After save → onCloseCatchmentPopup clears catchmentSettingsTemp
     * back to initial state and this effect would otherwise wipe the polygon
     * layers that the committed-state effect just put down.
     * Same as the committed effect: polygon dataset layers must follow the
     * active tab. Switching to BY_LOCATION / BY_POLYGON wipes them; switching
     * back to BY_DATASET re-renders them.
     */
    if (isBVTDataForCatchmentEnabled && showCatchmentPopup) {
      renderPolygonCatchmentDatasets(
        catchmentSettingsTemp?.catchmentInputBy === ECatchmentInputBy.BY_DATASET
          ? (catchmentSettingsTemp?.datasetConfig?.datasets ?? {})
          : {}
      );
    }
    if (catchmentSettingsTemp?.catchmentType === ECatchmentType.BY_RADIUS) {
      handleAddCircleRadiusTemp(catchmentSettingsTemp?.catchmentInputBy);
    } else {
      handleAddIsochroneTemp();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isMapLoaded,
    showCatchmentPopup,
    spatialAreaInputType,
    catchmentSettingsTemp?.catchmentInputBy,
    catchmentSettingsTemp?.catchmentType,
    catchmentSettingsTemp?.radiusData,
    catchmentSettingsTemp?.placeCoordinates,
    catchmentSettingsTemp.datasetConfig?.datasets,
    catchmentSettingsTemp?.roadAccessConfig?.isochroneData,
    showAnalysisResultPopup,
    isDrawPolygon
  ]);

  return {
    handleAddIsochroneTemp,
    handleAddCatchmentLayer,
    handleAddRegionLayer,
    handleAddIsochroneLayer,
    removeCatchmentMapDisplay
  };
};

export default useCatchmentArea;
